import { differenceInDays, format } from 'date-fns';
import { StackActions } from '@react-navigation/native';
import { updateUserAddress } from 'actions';
import { UPDATE_USER_ADDRESSES_SUCCESS } from 'actions/address';
import { selectIsIdentityLocked, selectPrimaryAddressForGB, selectSecondaryAddresses, selectUser, selectUserAdditionalInfo, } from 'reducers/selectors';
import { hasValidAddress, needsPreviousAddress as isRecentTenancy, } from 'features/credit-score/utils';
import { OnboardingFlow, } from 'utils/types/navigation';
const DATE_STORAGE_FORMAT = 'yyyy-MM-dd';
/** A number that has not been re-verified in this long needs confirming. */
const PHONE_STALE_AFTER_DAYS = 90;
export const selectMonevoStepContext = (state) => {
    const onboarding = state.monevoOnboarding;
    const secondaryAddresses = selectSecondaryAddresses(state);
    return {
        onboarding,
        user: selectUser(state),
        info: selectUserAdditionalInfo(state),
        isIdentityLocked: selectIsIdentityLocked(state),
        primaryAddress: selectPrimaryAddressForGB(state),
        previousAddress: secondaryAddresses.find((address) => address.id === onboarding.selectedPreviousAddressId),
        hasSecondaryAddresses: secondaryAddresses.length > 0,
    };
};
/**
 * Every address step reads its answer back out of the store to decide whether
 * it is satisfied, so the write has to land before the resolver runs —
 * otherwise it sends the user straight back to the screen they just answered.
 * A failed write leaves them on it, which is also what we want.
 */
const saveAddress = async ({ dispatch, next }, address, body) => {
    if (!address)
        return;
    const result = await dispatch(updateUserAddress(address.id, body));
    /**
     * The api middleware resolves with whichever action it dispatched, so a
     * failure comes back truthy — only the type tells the two apart.
     */
    if (result?.type !== UPDATE_USER_ADDRESSES_SUCCESS)
        return;
    next();
};
/**
 * Under three years at the current address, unless the user has told us there
 * is no earlier UK address to give.
 */
const needsPreviousAddress = (ctx) => isRecentTenancy(ctx.primaryAddress) && !ctx.onboarding.noPreviousAddress;
const isPhoneStale = (ctx) => !ctx.user.phoneNumberUpdatedAt ||
    differenceInDays(new Date(), ctx.user.phoneNumberUpdatedAt) >
        PHONE_STALE_AFTER_DAYS;
export const MONEVO_STEPS = [
    {
        id: 'loanAmount',
        route: 'LoanAmount',
        isApplicable: ({ onboarding }) => onboarding.type !== 'CREDIT_CARD',
    },
    {
        id: 'userInfo',
        route: 'UserInfo',
        /**
         * A KYC'd user's legal name and date of birth are locked, so there is
         * nothing on this screen left for them to answer.
         */
        isSatisfied: ({ user, isIdentityLocked }) => isIdentityLocked &&
            !!user.title &&
            !!user.firstName &&
            !!user.lastName &&
            !!user.dateOfBirth,
    },
    {
        id: 'phoneVerification',
        route: 'PhoneVerification',
        ownedRoutes: ['PhoneVerificationConfirm'],
        isSatisfied: ({ user }) => !!user.phoneNumber,
        params: ({ next, navigation }) => ({
            flow: 'monevo',
            title: 'Enter your phone number',
            subTitle: 'We need your phone number for regulatory requirements',
            onSuccess: () => {
                navigation.dispatch(StackActions.pop(2));
                next();
            },
        }),
    },
    {
        id: 'contactDetails',
        route: 'MonevoContactDetails',
        isApplicable: (ctx) => isPhoneStale(ctx) || !ctx.user.phoneNumber?.startsWith('+44'),
    },
    {
        id: 'familyDetails',
        route: 'MonevoFamilyDetails',
    },
    {
        id: 'primaryAddress',
        route: 'AddressPostCodeScreen',
        ownedRoutes: ['ChooseAddressScreen', 'AddressInputScreen'],
        isSatisfied: ({ primaryAddress }) => hasValidAddress(primaryAddress),
        params: () => ({ flow: OnboardingFlow.CreditCard }),
    },
    { id: 'reviewAddress', route: 'MonevoReviewAddress' },
    {
        id: 'residentType',
        route: 'MonevoResidentType',
        isSatisfied: ({ primaryAddress }) => !!primaryAddress?.residenceType,
        params: (args) => ({
            onSelect: (residenceType) => saveAddress(args, args.ctx.primaryAddress, { residenceType }),
        }),
    },
    {
        id: 'moveInDate',
        route: 'AddressDateScreen',
        isSatisfied: ({ primaryAddress }) => !!primaryAddress?.startDate,
        params: (args) => ({
            mode: 'moveIn',
            startDate: args.ctx.primaryAddress?.startDate,
            endDate: args.ctx.primaryAddress?.endDate,
            initialDate: args.ctx.primaryAddress?.startDate,
            onSave: (date) => saveAddress(args, args.ctx.primaryAddress, {
                startDate: format(date, DATE_STORAGE_FORMAT),
            }),
        }),
    },
    {
        id: 'choosePreviousAddress',
        route: 'MonevoChoosePreviousAddress',
        ownedRoutes: ['AddressPostCodeScreen'],
        isApplicable: (ctx) => needsPreviousAddress(ctx) && ctx.hasSecondaryAddresses,
        isSatisfied: ({ previousAddress }) => !!previousAddress &&
            !!previousAddress.startDate &&
            !!previousAddress.endDate &&
            !!previousAddress.residenceType,
    },
    {
        // Nothing on file to pick from, so go straight to entering one.
        id: 'addPreviousAddress',
        route: 'AddressPostCodeScreen',
        ownedRoutes: ['ChooseAddressScreen', 'AddressInputScreen'],
        isApplicable: (ctx) => needsPreviousAddress(ctx) && !ctx.hasSecondaryAddresses,
        isSatisfied: ({ previousAddress }) => !!previousAddress,
        params: () => ({
            isAddingSecondaryAddress: true,
            flow: OnboardingFlow.CreditCard,
        }),
    },
    {
        id: 'previousResidentType',
        route: 'MonevoResidentType',
        isApplicable: needsPreviousAddress,
        isSatisfied: ({ previousAddress }) => !!previousAddress?.residenceType,
        params: (args) => ({
            onSelect: (residenceType) => saveAddress(args, args.ctx.previousAddress, { residenceType }),
        }),
    },
    {
        id: 'previousMoveInDate',
        route: 'AddressDateScreen',
        isApplicable: needsPreviousAddress,
        isSatisfied: ({ previousAddress }) => !!previousAddress?.startDate,
        params: (args) => ({
            mode: 'moveIn',
            startDate: args.ctx.previousAddress?.startDate,
            endDate: args.ctx.previousAddress?.endDate,
            initialDate: args.ctx.previousAddress?.startDate,
            onSave: (date) => saveAddress(args, args.ctx.previousAddress, {
                startDate: format(date, DATE_STORAGE_FORMAT),
            }),
        }),
    },
    {
        id: 'previousMoveOutDate',
        route: 'AddressDateScreen',
        isApplicable: needsPreviousAddress,
        isSatisfied: ({ previousAddress }) => !!previousAddress?.endDate,
        params: (args) => ({
            mode: 'moveOut',
            startDate: args.ctx.previousAddress?.startDate,
            endDate: args.ctx.previousAddress?.endDate,
            initialDate: args.ctx.previousAddress?.endDate,
            onSave: (date) => saveAddress(args, args.ctx.previousAddress, {
                endDate: format(date, DATE_STORAGE_FORMAT),
            }),
        }),
    },
    { id: 'confirmAddress', route: 'MonevoConfirmAddress' },
    { id: 'userIncome', route: 'UserIncome' },
    { id: 'userExpenses', route: 'UserExpenses' },
];
/**
 * Reads the step tag off a route instance's params. Untyped at the call site
 * because a stack route's params widen to the union of every route in the
 * param list, which the tag is only part of.
 */
export const getMonevoStepId = (params) => {
    const stepId = params?.monevoStepId;
    return typeof stepId === 'string' ? stepId : undefined;
};
/**
 * Index to resolve forward from. `-1` (no match) means the whole table is
 * scanned, which is what entering the flow cold should do.
 *
 * Ids are matched ahead of route names: several routes sit at more than one
 * position in the table, so a name alone always resolves to the first of them
 * — the tag is the only thing that tells the positions apart.
 */
export const findMonevoStepIndex = (stepIdOrRoute) => {
    const byId = MONEVO_STEPS.findIndex((step) => step.id === stepIdOrRoute);
    if (byId > -1)
        return byId;
    return MONEVO_STEPS.findIndex((step) => step.route === stepIdOrRoute ||
        step.ownedRoutes?.includes(stepIdOrRoute));
};
export const findNextMonevoStep = (ctx, fromIndex) => MONEVO_STEPS.find((step, index) => index > fromIndex &&
    (step.isApplicable?.(ctx) ?? true) &&
    !(step.isSatisfied?.(ctx) ?? false));
